import { z } from 'zod';
import { CalendarDate } from './project.js';
import { ProjectCommitment } from './project-commitment.js';
import { ProjectRole } from './rbac.js';
/**
 * Link mời vào dự án — APM-1317b.
 *
 * ## ⚠️ Link chỉ dùng được cho người ĐÃ CÓ tài khoản trong tổ chức
 *
 * Lựa chọn có chủ đích. Link mời là một **URL mang quyền**: nếu nó vừa tạo tài
 * khoản vừa cho vào dự án thì một link lọt ra ngoài là một người lạ có tài
 * khoản thật trong tổ chức. Bắt đăng nhập trước biến link thành thứ chỉ **tiết
 * kiệm việc đi tìm tên**, không phải thứ mở cửa.
 */
/** Một link còn sống, **không** kèm chuỗi token. */
export const ProjectInviteView = z.object({
    expiresAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    /**
     * Role preselected on the sign-up form of whoever opens the link — APM-PSPACE-l.
     * `null` = they pick from scratch (as before). Still a wish: the PM settles the
     * role when finalizing.
     */
    roleId: z.uuid().nullable(),
});
/**
 * Một người đã đăng ký qua một link — APM-INVL-a. `status` là trạng thái **đơn**:
 * `accepted` = đã vào dự án, `pending` = chờ PM duyệt, `declined` = bị từ chối,
 * `withdrawn` = tự rút (chỉ đơn thành viên). Chỉ có đơn từ sau APM-INVL-a — đơn cũ
 * không ghi link nào.
 */
export const InviteJoiner = z.object({
    userId: z.uuid(),
    name: z.string(),
    status: z.enum(['pending', 'accepted', 'declined', 'withdrawn']),
    /** Lúc gửi đơn. */
    at: z.iso.datetime(),
});
/**
 * Một link mời thành viên còn sống, trong danh sách của mục Link mời — APM-INVL-a.
 * Nhiều link song song được (người dùng chốt 2026-10-15): mỗi link gửi một nhóm, và
 * biết ai vào bằng link nào. Thu hồi theo **id** từng link.
 */
export const ProjectInviteItem = ProjectInviteView.extend({
    id: z.uuid(),
    /** URL đầy đủ để sao chép lại. `null` = link tạo trước APM-INVL-a — chỉ có hash. */
    url: z.string().nullable(),
    joiners: z.array(InviteJoiner),
});
export const ProjectInviteList = z.object({ invites: z.array(ProjectInviteItem) });
/**
 * Create a project link with a default role — APM-PSPACE-l. Any role of the
 * project (staff, no ceiling). `.default({})`: callers from before send no body.
 */
export const CreateProjectInviteInput = z
    .object({ roleId: z.uuid().nullable().optional() })
    .default({});
/**
 * Change the default role of the **live** link without re-issuing it — APM-PSPACE-l.
 * Shared by the project link and the guest link. `null` = no default (as before).
 * Re-issuing would kill a link already handed out just to change a dropdown.
 */
export const SetInviteRoleInput = z.object({ roleId: z.uuid().nullable() });
/**
 * Phản hồi của lần tạo link, kèm chuỗi thật.
 *
 * Từ APM-INVL-a token còn được lưu **mã hoá**, nên `GET /projects/:id/invites` đọc
 * lại được URL; tạo link mới **không** thu hồi link cũ — nhiều link song song, mỗi
 * link thu hồi theo id, và mục Link mời liệt kê hết nên không link nào bị quên.
 */
export const ProjectInviteCreated = ProjectInviteView.extend({
    token: z.string(),
});
/**
 * Vỏ cho câu trả lời "có link nào đang sống không".
 *
 * ⚠️ Không trả `null` trần: Nest serialize `null` thành **body rỗng**,
 * `apiFetch` đọc body rỗng thành `undefined`, và React Query từ chối
 * `undefined` ⇒ cả query hỏng — đúng ở trạng thái **thường gặp nhất** của
 * endpoint này ("chưa tạo link nào"). "Chưa có" phải là một giá trị, không phải
 * một khoảng trống.
 */
export const ProjectInviteState = z.object({
    invite: ProjectInviteView.nullable(),
});
/**
 * Token đi trong **body**, không trong URL.
 *
 * Cùng lý do như `POST /auth/reset-password`: URL bị ghi vào access log của
 * proxy, vào history trình duyệt, và vào `Referer`. Token trong log là token bị
 * lộ. Đường dẫn `/invite/:token` ở trình duyệt là không tránh được — đó chính
 * là cái link — nhưng lời gọi API thì tránh được.
 */
export const InviteTokenInput = z.object({
    token: z.string().min(16).max(200),
});
/**
 * Vì sao một link không dùng được — để màn hình nói đúng câu.
 *
 * `not_found` gộp cả "token sai" và "token của tổ chức khác": phân biệt hai thứ
 * đó cho người đang giữ một token lạ là nói cho họ biết token ấy **có tồn tại**
 * ở đâu đó, và tên dự án nào đứng sau nó.
 */
export const InviteStatus = z.enum([
    'ok',
    'already_member',
    /** Đã đăng ký rồi, đang chờ PM chốt — APM-ROLE-p. Đăng ký lần hai là no-op. */
    'pending_request',
    /**
     * PM đã mời **đích danh** người này và đang chờ **họ** trả lời — APM-ROLE-p2.
     *
     * Tách khỏi `pending_request`: cả hai đều là một hàng chờ, nhưng người phải
     * làm gì đó ở hai phía ngược nhau. Gộp lại thì người được mời đọc "đang chờ
     * duyệt" và ngồi chờ PM, còn PM chờ họ nhận lời (2026-09-28, dự án SEED).
     * `invitationId` đi kèm để trang trả lời được ngay tại chỗ.
     */
    'invited',
    'expired',
    'revoked',
    'not_found',
]);
/**
 * Một vai trò để người đăng ký chọn — APM-ROLE-p.
 *
 * Cố tình **không** mang danh sách quyền, khác `ProjectRoleView`. Người đang xem
 * chưa ở trong dự án: cho họ đọc bản đồ quyền của dự án là để một token rò ra
 * ngoài trở thành một bản mô tả ai làm được gì ở đó.
 */
export const InviteRoleOption = z.object({
    id: z.uuid(),
    /** `null` = vai trò tuỳ biến của dự án đó. */
    key: ProjectRole.nullable(),
    name: z.string(),
});
export const InvitePreview = z.object({
    status: InviteStatus,
    /** `null` với mọi trạng thái mà người xem không được biết dự án nào. */
    projectId: z.uuid().nullable(),
    projectKey: z.string().nullable(),
    projectName: z.string().nullable(),
    /**
     * Mục tiêu và thời gian dự án — APM-ROLE-p.
     *
     * Người ta đang quyết bỏ một phần thời gian của mình vào đây; một cái tên dự
     * án không đủ để quyết. `null` ở cả ba khi người xem chưa được biết dự án nào.
     */
    projectDescription: z.string().nullable(),
    projectStartDate: CalendarDate.nullable(),
    projectEndDate: CalendarDate.nullable(),
    /**
     * Vai trò để chọn nguyện vọng. Rỗng với mọi trạng thái không phải `ok` — hết
     * hạn hay đã thu hồi thì không có gì để chọn.
     */
    roles: z.array(InviteRoleOption),
    /** Lời mời của PM đang chờ người xem trả lời — chỉ có ở `invited`. */
    invitationId: z.uuid().nullable(),
    /**
     * The link's default role, preselected on the form — APM-PSPACE-l. Only at `ok`,
     * and only when it is one of `roles`; `null` otherwise.
     */
    defaultRoleId: z.uuid().nullable(),
});
/**
 * Đăng ký tham gia qua link — APM-ROLE-p.
 *
 * ## ⚠️ Đây là NGUYỆN VỌNG, không phải quyết định
 *
 * Bấm nút này **không** đưa ai vào dự án: nó tạo một bản ghi ở chiều
 * `initiated_by = 'user'` và PM chốt lại vai trò, khoảng ngày, thời lượng trước
 * khi chấp nhận. Nếu bấm là vào thẳng với vai trò tự chọn thì link mời trở
 * thành một đường **tự cấp quyền** — ai giữ link cũng tự phong mình làm PM.
 */
export const ApplyToProjectInput = InviteTokenInput.extend({
    /** Vai trò mong muốn, phải thuộc **đúng dự án của link này**. */
    roleId: z.uuid(),
    fromDate: CalendarDate.nullable().optional(),
    toDate: CalendarDate.nullable().optional(),
    commitment: ProjectCommitment.nullable().optional(),
});
/** Bảy ngày — xem chú thích ở `expiresAt` của bảng `project_invite`. */
export const INVITE_TTL_DAYS = 7;
//# sourceMappingURL=project-invite.js.map